import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useAuth, useUser } from "@clerk/clerk-react";
import { backendFetch } from "@/lib/backend";

export type OnboardingProfile = {
  userId: string;
  email?: string | null;
  displayName?: string | null;
  useCase?: string | null;
  discoverySource?: string | null;
  primaryGoal?: string | null;
  tutorialCompleted: boolean;
  onboardingCompleted: boolean;
};

type OnboardingInput = {
  useCase: string;
  discoverySource: string;
  primaryGoal: string;
  tutorialCompleted?: boolean;
  onboardingCompleted?: boolean;
};

type OnboardingContextType = {
  profile: OnboardingProfile | null;
  loading: boolean;
  refreshProfile: () => Promise<void>;
  completeOnboarding: (input: OnboardingInput) => Promise<void>;
};

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

const profileCacheKey = (userId: string) => `orizonte:onboarding-profile:${userId}`;

const readCachedProfile = (userId: string): OnboardingProfile | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(profileCacheKey(userId));
    return raw ? JSON.parse(raw) as OnboardingProfile : null;
  } catch {
    return null;
  }
};

const writeCachedProfile = (profile: OnboardingProfile) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(profileCacheKey(profile.userId), JSON.stringify(profile));
  } catch {
    // Local persistence is best-effort; the server remains the source of truth.
  }
};

const mergeWithCachedProfile = (serverProfile: OnboardingProfile, cachedProfile: OnboardingProfile | null) => {
  if (!cachedProfile?.onboardingCompleted || serverProfile.onboardingCompleted) return serverProfile;
  return {
    ...serverProfile,
    useCase: serverProfile.useCase || cachedProfile.useCase,
    discoverySource: serverProfile.discoverySource || cachedProfile.discoverySource,
    primaryGoal: serverProfile.primaryGoal || cachedProfile.primaryGoal,
    tutorialCompleted: serverProfile.tutorialCompleted || cachedProfile.tutorialCompleted,
    onboardingCompleted: true,
  };
};

export const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
  const { user } = useUser();
  const { isLoaded, isSignedIn } = useAuth();
  const [profile, setProfile] = useState<OnboardingProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  const refreshProfile = useCallback(async (forceRefresh = false) => {
    if (!isLoaded) {
      setLoading(true);
      return;
    }

    if (!isSignedIn || !user) {
      setProfile(null);
      hasLoadedOnce.current = false;
      setLoading(false);
      return;
    }

    const cachedProfile = readCachedProfile(user.id);
    if (!hasLoadedOnce.current && cachedProfile) {
      setProfile(cachedProfile);
      hasLoadedOnce.current = true;
    }

    setLoading(!hasLoadedOnce.current);
    try {
      const data = await backendFetch("/profile", {
        forceRefresh,
        staleTtlMs: 10 * 60_000,
      });
      const nextProfile = mergeWithCachedProfile(data.profile, cachedProfile);
      setProfile(nextProfile);
      writeCachedProfile(nextProfile);
      hasLoadedOnce.current = true;
    } catch {
      // Keep the current/cached profile so a transient refresh failure does not restart onboarding.
    } finally {
      setLoading(false);
    }
  }, [isLoaded, isSignedIn, user]);

  useEffect(() => {
    void refreshProfile();
    const backgroundRefresh = window.setTimeout(() => refreshProfile(true), 300);
    return () => window.clearTimeout(backgroundRefresh);
  }, [refreshProfile]);

  const completeOnboarding = async (input: OnboardingInput) => {
    if (!user) throw new Error("You must be signed in to complete onboarding.");

    const optimisticProfile: OnboardingProfile = {
      userId: user.id,
      email: user.primaryEmailAddress?.emailAddress ?? null,
      displayName: user.fullName ?? user.firstName ?? null,
      useCase: input.useCase,
      discoverySource: input.discoverySource,
      primaryGoal: input.primaryGoal,
      tutorialCompleted: input.tutorialCompleted ?? true,
      onboardingCompleted: input.onboardingCompleted ?? true,
    };
    setProfile(optimisticProfile);
    writeCachedProfile(optimisticProfile);

    try {
      const data = await backendFetch(
        "/profile",
        {
          method: "PATCH",
          body: JSON.stringify({
            ...input,
            tutorialCompleted: input.tutorialCompleted ?? true,
            onboardingCompleted: input.onboardingCompleted ?? true,
          }),
        }
      );
      const nextProfile = mergeWithCachedProfile(data.profile, optimisticProfile);
      setProfile(nextProfile);
      writeCachedProfile(nextProfile);
    } catch {
      // The local profile is already marked complete, so a transient API failure should not block dashboard access.
    }
  };

  return (
    <OnboardingContext.Provider value={{ profile, loading, refreshProfile, completeOnboarding }}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
